import React from 'react';
import { Target, TrendUp, Lightning, Leaf, Drop, Factory } from '@phosphor-icons/react';

const PILOT_CONFIG = {
  oee_improvement_points: { label: 'OEE Improvement', unit: 'pp', icon: Factory, color: '#4ade80' },
  energy_cost_reduction_pct: { label: 'Energy Cost Reduction', unit: '%', icon: Lightning, color: '#facc15' },
  scrap_rate_reduction_pp: { label: 'Scrap Rate Reduction', unit: 'pp', icon: Target, color: '#f97316' },
  utility_downtime_reduction_pct: { label: 'Utility Downtime Reduction', unit: '%', icon: TrendUp, color: '#60a5fa' },
  peak_electricity_reduction_pct: { label: 'Peak Electricity Reduction', unit: '%', icon: Lightning, color: '#c084fc' },
  co2e_per_unit_reduction_pct: { label: 'CO2e per Unit Reduction', unit: '%', icon: Leaf, color: '#2dd4bf' },
};

export default function OeePilot({ data }) {
  const targets = data?.pilot_targets || {};
  if (!Object.keys(targets).length) return null;

  const entries = Object.entries(PILOT_CONFIG).map(([key, cfg]) => {
    const t = targets[key] || {};
    const current = t.current || 0;
    const low = t.target_low || 0;
    const high = t.target_high || 0;
    const pctOfHigh = high > 0 ? Math.min(current / high * 100, 120) : 0;
    const lowPct = high > 0 ? (low / high * 100) : 0;
    const status = current >= high ? 'Exceeded' : current >= low ? 'On Track' : 'Behind';
    const statusColor = status === 'Exceeded' ? '#4ade80' : status === 'On Track' ? '#facc15' : '#ef4444';
    return { key, ...cfg, current, low, high, pctOfHigh, lowPct, status, statusColor };
  });

  const onTrack = entries.filter(e => e.status !== 'Behind').length;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="oee-pilot">
      {/* Summary */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-base font-semibold text-text-primary">90-Day Pilot Progress</h2>
            <p className="text-xs font-body text-text-secondary mt-1">Tracking 6 key performance indicators against conservative and stretch targets</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center">
              <p className="font-heading text-2xl font-bold text-text-primary">{onTrack}/{entries.length}</p>
              <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">On Track</p>
            </div>
            <div className="w-12 h-12 rounded-full border-4 flex items-center justify-center" style={{
              borderColor: onTrack >= entries.length ? '#4ade80' : onTrack >= entries.length / 2 ? '#facc15' : '#ef4444',
            }}>
              <span className="font-heading text-sm font-bold text-text-primary">{Math.round(onTrack / entries.length * 100)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {entries.map((e) => {
          const Icon = e.icon;
          return (
            <div key={e.key} className="bg-surface border border-border rounded-lg p-5 hover:border-tertiary/40 transition-all" data-testid={`oee-pilot-${e.key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: e.color + '18' }}>
                    <Icon size={16} weight="duotone" style={{ color: e.color }} />
                  </div>
                  <div>
                    <p className="text-xs font-body font-semibold text-text-primary">{e.label}</p>
                  </div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-body font-semibold" style={{ backgroundColor: e.statusColor + '20', color: e.statusColor }}>
                  {e.status}
                </span>
              </div>

              {/* Current value */}
              <p className="font-heading text-3xl font-bold text-text-primary mb-1">{e.current} <span className="text-base text-text-secondary">{e.unit}</span></p>

              {/* Progress bar */}
              <div className="relative h-3 bg-background rounded-full overflow-visible mb-2 mt-3">
                {/* Low target marker */}
                <div className="absolute top-0 h-3 border-l-2 border-dashed border-amber-400/60" style={{ left: `${Math.min(e.lowPct, 100)}%` }} />
                {/* High target marker */}
                <div className="absolute top-0 h-3 border-l-2 border-dashed border-emerald-400/60" style={{ left: '100%' }} />
                {/* Progress fill */}
                <div className="h-full rounded-full transition-all duration-700" style={{
                  width: `${Math.min(e.pctOfHigh, 100)}%`,
                  backgroundColor: e.statusColor,
                }} />
              </div>

              {/* Range labels */}
              <div className="flex justify-between text-[10px] font-body text-text-secondary">
                <span>0</span>
                <span className="text-amber-400">Low: {e.low}{e.unit}</span>
                <span className="text-emerald-400">High: {e.high}{e.unit}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="bg-surface border border-border rounded-lg p-4 flex flex-wrap items-center gap-6 text-xs font-body">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-emerald-400" /> Exceeded — Current exceeds stretch target</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-amber-400" /> On Track — Current within low-high range</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-red-400" /> Behind — Current below conservative target</span>
      </div>
    </div>
  );
}
